import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Chip,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  Skeleton,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Checkbox,
  Button,
  Collapse,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ExtensionIcon from '@mui/icons-material/Extension';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { jsPDF } from 'jspdf';
import { getApps } from '../services/api';
import AdminGate from '../components/AdminGate';

const BASE_COLOR = '#0f62fe';
const PROVIDER_COLOR = '#ff832b';
const ITEMS_COLOR = '#ee5396';

const normalizeLine = (it) => ({
  id: it && it.id ? it.id : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  name: (it && it.name) || '',
  description: (it && it.description) || '',
  monthly: Number(it && it.monthly) || 0,
});

const normalizeCosts = (raw) => {
  const out = { base: [], provider: { name: '', monthly: 0 }, items: [] };
  if (!raw) return out;
  return {
    base: Array.isArray(raw.base) ? raw.base.map(normalizeLine) : [],
    provider: { ...out.provider, ...(raw.provider || {}) },
    items: Array.isArray(raw.items) ? raw.items.map(normalizeLine) : [],
  };
};

const sumLines = (lines) => lines.reduce((s, it) => s + Number(it.monthly || 0), 0);

const baseTotal = (raw) => {
  const c = normalizeCosts(raw);
  return sumLines(c.base) + Number(c.provider.monthly || 0);
};

const hasAnyCosts = (raw) => {
  const c = normalizeCosts(raw);
  return c.base.length > 0 || Number(c.provider.monthly || 0) > 0 || c.items.length > 0;
};

const fmtCOP = (n, { compact = false } = {}) =>
  new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    maximumFractionDigits: 0,
    notation: compact ? 'compact' : 'standard',
  }).format(n || 0);

const fmtDate = (d = new Date()) =>
  d.toLocaleDateString('es-CO', { year: 'numeric', month: 'long', day: 'numeric' });

const CostLine = ({ color, label, value }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="center">
    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
      <Typography variant="caption" color="text.secondary" noWrap>{label}</Typography>
    </Stack>
    <Typography variant="caption" sx={{ fontWeight: 500, fontVariantNumeric: 'tabular-nums', flexShrink: 0, ml: 1 }}>
      {fmtCOP(value)}
    </Typography>
  </Stack>
);

const AppEstimatorCard = ({ app, selected, selectedItems, onToggleApp, onToggleItem }) => {
  const c = normalizeCosts(app.costs);
  const base = baseTotal(app.costs);
  const selectedItemsTotal = c.items
    .filter((it) => selectedItems.includes(it.id))
    .reduce((s, it) => s + Number(it.monthly || 0), 0);
  const total = (selected ? base : 0) + selectedItemsTotal;
  const noCosts = !hasAnyCosts(app.costs);

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: '14px',
        border: '1px solid',
        borderColor: selected ? BASE_COLOR : '#e0e3e7',
        backgroundColor: selected ? '#f6faff' : 'white',
        transition: 'all 0.2s',
        overflow: 'hidden',
        '&:hover': { borderColor: selected ? BASE_COLOR : '#9da3a8' },
      }}
    >
      <Box
        sx={{
          p: 2,
          cursor: noCosts ? 'not-allowed' : 'pointer',
          opacity: noCosts ? 0.55 : 1,
        }}
        onClick={() => !noCosts && onToggleApp(app.id)}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Checkbox
            checked={selected}
            disabled={noCosts}
            onClick={(e) => e.stopPropagation()}
            onChange={() => onToggleApp(app.id)}
            icon={<RadioButtonUncheckedIcon />}
            checkedIcon={<CheckCircleIcon sx={{ color: BASE_COLOR }} />}
            sx={{ p: 0.5 }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.2 }} noWrap>
                {app.name}
              </Typography>
              {app.category && (
                <Chip
                  label={app.category}
                  size="small"
                  sx={{ height: 18, fontSize: '0.65rem', backgroundColor: '#f4f4f4', color: '#525252' }}
                />
              )}
            </Stack>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Typography variant="caption" color="text.secondary">
                Base: <strong style={{ color: '#161616', fontVariantNumeric: 'tabular-nums' }}>{fmtCOP(base)}</strong>/mes
              </Typography>
              {c.items.length > 0 && (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <ExtensionIcon sx={{ fontSize: 12, color: ITEMS_COLOR }} />
                  <Typography variant="caption" color="text.secondary">
                    {c.items.length} item{c.items.length !== 1 ? 's' : ''} disp.
                  </Typography>
                </Stack>
              )}
              {noCosts && (
                <Typography variant="caption" sx={{ color: '#fa4d56' }}>
                  Sin costos cargados
                </Typography>
              )}
            </Stack>
          </Box>
          {selected && (
            <Typography variant="body2" sx={{ fontWeight: 700, color: BASE_COLOR, fontVariantNumeric: 'tabular-nums' }}>
              {fmtCOP(total)}
            </Typography>
          )}
        </Stack>
      </Box>

      <Collapse in={selected} unmountOnExit>
        <Divider />
        <Box sx={{ p: 2, backgroundColor: 'white' }}>
          {(c.base.length > 0 || Number(c.provider.monthly || 0) > 0) && (
            <>
              <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: '0.08em', fontSize: '0.65rem' }}>
                Incluido en el costo base
              </Typography>
              <Stack spacing={0.75} sx={{ mt: 0.5, mb: c.items.length > 0 ? 2 : 0 }}>
                {c.base.map((line) => (
                  <Box key={line.id}>
                    <CostLine color={BASE_COLOR} label={line.name} value={line.monthly} />
                    {line.description && (
                      <Typography variant="caption" sx={{ color: 'text.disabled', ml: 2.25, display: 'block' }}>
                        {line.description}
                      </Typography>
                    )}
                  </Box>
                ))}
                {Number(c.provider.monthly || 0) > 0 && (
                  <CostLine
                    color={PROVIDER_COLOR}
                    label={`Proveedor${c.provider.name ? ` · ${c.provider.name}` : ''}`}
                    value={c.provider.monthly || 0}
                  />
                )}
              </Stack>
            </>
          )}

          {c.items.length > 0 && (
            <>
              <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: '0.08em', fontSize: '0.65rem' }}>
                Items adicionales (opcionales)
              </Typography>
              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                {c.items.map((it) => {
                  const checked = selectedItems.includes(it.id);
                  return (
                    <Paper
                      key={it.id}
                      variant="outlined"
                      onClick={(e) => { e.stopPropagation(); onToggleItem(app.id, it.id); }}
                      sx={{
                        p: 1.25,
                        cursor: 'pointer',
                        borderRadius: '8px',
                        borderColor: checked ? ITEMS_COLOR : '#e0e3e7',
                        backgroundColor: checked ? '#fdf2f7' : 'white',
                        transition: 'all 0.15s',
                        '&:hover': { borderColor: ITEMS_COLOR },
                      }}
                    >
                      <Stack direction="row" spacing={1.25} alignItems="center">
                        <Checkbox
                          checked={checked}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => onToggleItem(app.id, it.id)}
                          size="small"
                          sx={{
                            p: 0,
                            color: '#9da3a8',
                            '&.Mui-checked': { color: ITEMS_COLOR },
                          }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
                            {it.name}
                          </Typography>
                          {it.description && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                              {it.description}
                            </Typography>
                          )}
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: checked ? ITEMS_COLOR : 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                          {fmtCOP(it.monthly)}
                        </Typography>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            </>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

const CostosContent = () => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [period, setPeriod] = useState('monthly');
  const [selection, setSelection] = useState({});

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await getApps();
        if (alive) setApps(data);
      } catch (err) {
        // empty
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const toggleApp = (appId) => {
    setSelection((prev) => {
      const next = { ...prev };
      if (appId in next) delete next[appId];
      else next[appId] = [];
      return next;
    });
  };

  const toggleItem = (appId, itemId) => {
    setSelection((prev) => {
      const current = prev[appId] || [];
      const has = current.includes(itemId);
      return {
        ...prev,
        [appId]: has ? current.filter((x) => x !== itemId) : [...current, itemId],
      };
    });
  };

  const reset = () => setSelection({});

  const categories = useMemo(() => {
    const set = new Set(apps.map((a) => a.category).filter(Boolean));
    return ['all', ...Array.from(set).sort()];
  }, [apps]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return apps.filter((a) => {
      if (a.visibleInCatalog === false) return false;
      if (category !== 'all' && a.category !== category) return false;
      if (!term) return true;
      return (
        (a.name || '').toLowerCase().includes(term) ||
        (a.shortName || '').toLowerCase().includes(term) ||
        (a.category || '').toLowerCase().includes(term)
      );
    });
  }, [apps, search, category]);

  const selectedDetail = useMemo(() => {
    return Object.entries(selection).map(([appId, itemIds]) => {
      const app = apps.find((a) => a.id === appId);
      if (!app) return null;
      const c = normalizeCosts(app.costs);
      const base = baseTotal(app.costs);
      const items = c.items.filter((it) => itemIds.includes(it.id));
      const itemsSum = items.reduce((s, it) => s + Number(it.monthly || 0), 0);
      return { app, c, base, items, itemsSum, total: base + itemsSum };
    }).filter(Boolean);
  }, [selection, apps]);

  const grandTotal = useMemo(
    () => selectedDetail.reduce((s, x) => s + x.total, 0),
    [selectedDetail]
  );

  const periodMultiplier = period === 'annual' ? 12 : 1;
  const displayTotal = grandTotal * periodMultiplier;

  const exportPdf = () => {
    if (selectedDetail.length === 0) return;
    const doc = new jsPDF({ unit: 'pt', format: 'letter' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const M = 48;
    let y = M;

    doc.setFillColor(15, 98, 254);
    doc.rect(0, 0, pageW, 60, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text('Cotización de operación', M, 38);
    doc.setFont(undefined, 'normal');
    doc.setFontSize(9);
    doc.text(fmtDate(), pageW - M, 38, { align: 'right' });
    y = 90;

    doc.setTextColor(33, 33, 33);
    doc.setFontSize(10);
    doc.text(`Periodo: ${period === 'annual' ? 'Anual' : 'Mensual'} · Moneda: COP`, M, y);
    doc.text(`Herramientas: ${selectedDetail.length}`, pageW - M, y, { align: 'right' });
    y += 22;

    const ensureRoom = (need) => {
      if (y + need > pageH - M) {
        doc.addPage();
        y = M;
      }
    };

    selectedDetail.forEach(({ app, c, base, items, itemsSum, total }, idx) => {
      ensureRoom(120);

      doc.setFillColor(244, 244, 244);
      doc.rect(M, y, pageW - M * 2, 26, 'F');
      doc.setTextColor(22, 22, 22);
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.text(`${idx + 1}. ${app.name}`, M + 10, y + 17);
      doc.setFont(undefined, 'normal');
      doc.setFontSize(9);
      doc.setTextColor(99, 99, 99);
      if (app.category) doc.text(app.category, M + 200, y + 17);
      doc.setTextColor(15, 98, 254);
      doc.setFont(undefined, 'bold');
      doc.text(fmtCOP(total * periodMultiplier), pageW - M - 10, y + 17, { align: 'right' });
      y += 34;

      doc.setFont(undefined, 'normal');
      doc.setTextColor(33, 33, 33);
      doc.setFontSize(9);

      const baseRows = [
        ...c.base.map((it) => [it.name, it.monthly || 0]),
      ];
      if (Number(c.provider.monthly || 0) > 0) {
        baseRows.push([`Proveedor${c.provider.name ? ` (${c.provider.name})` : ''}`, c.provider.monthly]);
      }

      if (baseRows.length === 0) {
        ensureRoom(16);
        doc.setTextColor(150, 150, 150);
        doc.text('  (sin costos base definidos)', M + 10, y);
        y += 14;
      } else {
        baseRows.forEach(([label, val]) => {
          ensureRoom(16);
          doc.setTextColor(99, 99, 99);
          doc.text(`  · ${label}`, M + 10, y);
          doc.setTextColor(33, 33, 33);
          doc.text(fmtCOP(val * periodMultiplier), pageW - M - 10, y, { align: 'right' });
          y += 14;
        });
      }

      ensureRoom(20);
      y += 4;
      doc.setDrawColor(220, 220, 220);
      doc.line(M + 10, y, pageW - M - 10, y);
      y += 12;
      doc.setFont(undefined, 'bold');
      doc.text('  Subtotal base', M + 10, y);
      doc.text(fmtCOP(base * periodMultiplier), pageW - M - 10, y, { align: 'right' });
      doc.setFont(undefined, 'normal');
      y += 16;

      if (items.length > 0) {
        ensureRoom(20);
        doc.setFontSize(8);
        doc.setTextColor(99, 99, 99);
        doc.text('  ITEMS ADICIONALES SELECCIONADOS', M + 10, y);
        y += 12;
        doc.setFontSize(9);
        items.forEach((it) => {
          ensureRoom(it.description ? 28 : 16);
          doc.setTextColor(33, 33, 33);
          doc.text(`  + ${it.name}`, M + 10, y);
          doc.text(fmtCOP((it.monthly || 0) * periodMultiplier), pageW - M - 10, y, { align: 'right' });
          y += 12;
          if (it.description) {
            doc.setTextColor(99, 99, 99);
            doc.setFontSize(8);
            const wrapped = doc.splitTextToSize(it.description, pageW - M * 2 - 40);
            doc.text(wrapped, M + 22, y);
            y += wrapped.length * 10;
            doc.setFontSize(9);
          }
        });
        ensureRoom(16);
        doc.setFont(undefined, 'bold');
        doc.text('  Subtotal items', M + 10, y);
        doc.text(fmtCOP(itemsSum * periodMultiplier), pageW - M - 10, y, { align: 'right' });
        doc.setFont(undefined, 'normal');
        y += 16;
      }

      ensureRoom(24);
      doc.setFillColor(15, 98, 254);
      doc.setTextColor(255, 255, 255);
      doc.rect(M, y, pageW - M * 2, 22, 'F');
      doc.setFont(undefined, 'bold');
      doc.setFontSize(10);
      doc.text(`  Total ${app.name}`, M + 10, y + 15);
      doc.text(fmtCOP(total * periodMultiplier), pageW - M - 10, y + 15, { align: 'right' });
      doc.setFont(undefined, 'normal');
      y += 36;
    });

    ensureRoom(50);
    doc.setDrawColor(15, 98, 254);
    doc.setLineWidth(1.5);
    doc.line(M, y, pageW - M, y);
    y += 20;
    doc.setTextColor(33, 33, 33);
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text(`TOTAL ${period === 'annual' ? 'ANUAL' : 'MENSUAL'}`, M, y);
    doc.setTextColor(15, 98, 254);
    doc.setFontSize(14);
    doc.text(fmtCOP(displayTotal), pageW - M, y, { align: 'right' });

    if (period === 'monthly') {
      y += 16;
      doc.setFont(undefined, 'normal');
      doc.setFontSize(9);
      doc.setTextColor(99, 99, 99);
      doc.text('Proyección anual', M, y);
      doc.text(fmtCOP(grandTotal * 12), pageW - M, y, { align: 'right' });
    }

    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text('Cotización generada automáticamente · Team I+D · IBM Colombia', pageW / 2, pageH - 24, { align: 'center' });

    const fname = `cotizacion-${new Date().toISOString().slice(0, 10)}.pdf`;
    doc.save(fname);
  };

  const selectedCount = selectedDetail.length;

  return (
    <Box sx={{ minHeight: 'calc(100vh - 48px)', backgroundColor: '#f4f4f4' }}>
      <Box
        sx={{
          background: 'linear-gradient(135deg, #161616 0%, #1f1f24 60%, #2a2a37 100%)',
          color: 'white',
          pt: { xs: 5, md: 6 },
          pb: { xs: 5, md: 6 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -180,
            right: -120,
            width: 480,
            height: 480,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(15,98,254,0.28) 0%, transparent 70%)',
            filter: 'blur(40px)',
          },
        }}
      >
        <Container maxWidth="xl" sx={{ position: 'relative' }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: BASE_COLOR }} />
            <Typography variant="caption" sx={{ letterSpacing: '0.15em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)' }}>
              Simulador de PMs
            </Typography>
          </Stack>
          <Typography variant="h3" sx={{ fontWeight: 700, letterSpacing: '-0.03em', mb: 1.5, lineHeight: 1.1 }}>
            Cotizador I+D
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)', maxWidth: 680 }}>
            Selecciona las herramientas que necesitas, añade los items adicionales que requieras y obtén el costo estimado mensual o anual. Puedes exportar la cotización en PDF.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ mt: -3, mb: 3, position: 'relative' }}>
        <Paper
          elevation={0}
          sx={{ p: 2, borderRadius: '14px', border: '1px solid #e0e3e7', boxShadow: '0 4px 24px rgba(0,0,0,0.04)' }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} justifyContent="space-between">
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ flex: 1 }}>
              <TextField
                placeholder="Buscar herramienta…"
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }}
                sx={{ maxWidth: 280, flex: 1, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              />
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Categoría</InputLabel>
                <Select value={category} label="Categoría" onChange={(e) => setCategory(e.target.value)} sx={{ borderRadius: '10px' }}>
                  {categories.map((c) => (<MenuItem key={c} value={c}>{c === 'all' ? 'Todas' : c}</MenuItem>))}
                </Select>
              </FormControl>
            </Stack>
            <ToggleButtonGroup
              size="small"
              value={period}
              exclusive
              onChange={(_, v) => v && setPeriod(v)}
              sx={{ '& .MuiToggleButton-root': { borderRadius: '10px !important', px: 2, textTransform: 'none', fontWeight: 500 } }}
            >
              <ToggleButton value="monthly">Mensual</ToggleButton>
              <ToggleButton value="annual">Anual</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Paper>
      </Container>

      <Container maxWidth="xl" sx={{ pb: 6 }}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, lg: 8 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-end" sx={{ mb: 1.5 }}>
              <Box>
                <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: '0.1em' }}>
                  Paso 1
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Selecciona las herramientas
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                {filtered.length} disponible{filtered.length !== 1 ? 's' : ''}
              </Typography>
            </Stack>
            {loading ? (
              <Stack spacing={1.5}>
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} variant="rounded" height={72} sx={{ borderRadius: '14px' }} />
                ))}
              </Stack>
            ) : filtered.length === 0 ? (
              <Paper sx={{ p: 6, textAlign: 'center', borderRadius: '14px', border: '1px solid #e0e3e7' }}>
                <Typography variant="body2" color="text.secondary">No se encontraron herramientas con los filtros actuales.</Typography>
              </Paper>
            ) : (
              <Stack spacing={1.5}>
                {filtered.map((app) => (
                  <AppEstimatorCard
                    key={app.id}
                    app={app}
                    selected={app.id in selection}
                    selectedItems={selection[app.id] || []}
                    onToggleApp={toggleApp}
                    onToggleItem={toggleItem}
                  />
                ))}
              </Stack>
            )}
          </Grid>

          <Grid size={{ xs: 12, lg: 4 }}>
            <Box sx={{ position: { lg: 'sticky' }, top: { lg: 72 } }}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: '16px',
                  border: '1px solid #e0e3e7',
                  overflow: 'hidden',
                  boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
                }}
              >
                <Box sx={{ background: 'linear-gradient(135deg, #0f62fe 0%, #4589ff 100%)', color: 'white', p: 2.5 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <RequestQuoteIcon sx={{ fontSize: 18 }} />
                    <Typography variant="overline" sx={{ letterSpacing: '0.1em', color: 'rgba(255,255,255,0.85)' }}>
                      Tu cotización
                    </Typography>
                  </Stack>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    Total {period === 'annual' ? 'anual' : 'mensual'} estimado
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.1, mt: 0.5 }}>
                    {fmtCOP(displayTotal)}
                  </Typography>
                  {period === 'monthly' && grandTotal > 0 && (
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', display: 'block', mt: 0.5 }}>
                      Proyección anual: {fmtCOP(grandTotal * 12)}
                    </Typography>
                  )}
                </Box>

                <Box sx={{ p: 2.5 }}>
                  {selectedCount === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                      <KeyboardArrowDownIcon sx={{ color: '#9da3a8', fontSize: 24, mb: 0.5 }} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        Aún no has seleccionado herramientas
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Marca las que necesites para ver el total.
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                        {selectedCount} herramienta{selectedCount !== 1 ? 's' : ''}
                      </Typography>
                      <Stack spacing={1} sx={{ mt: 1, mb: 2 }}>
                        {selectedDetail.map(({ app, items, total }) => (
                          <Box key={app.id} sx={{ p: 1.25, borderRadius: '8px', backgroundColor: '#fafbfc', border: '1px solid #eef0f3' }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }} noWrap>
                                  {app.name}
                                </Typography>
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                  <Typography variant="caption" color="text.secondary">
                                    Base
                                  </Typography>
                                  {items.length > 0 && (
                                    <>
                                      <Box sx={{ width: 2, height: 2, borderRadius: '50%', backgroundColor: '#9da3a8' }} />
                                      <Typography variant="caption" sx={{ color: ITEMS_COLOR }}>
                                        +{items.length} item{items.length !== 1 ? 's' : ''}
                                      </Typography>
                                    </>
                                  )}
                                </Stack>
                              </Box>
                              <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                                {fmtCOP(total * periodMultiplier)}
                              </Typography>
                            </Stack>
                          </Box>
                        ))}
                      </Stack>
                      <Divider sx={{ my: 1.5 }} />
                    </>
                  )}

                  <Stack spacing={1}>
                    <Button
                      onClick={exportPdf}
                      disabled={selectedCount === 0}
                      variant="contained"
                      fullWidth
                      startIcon={<PictureAsPdfIcon />}
                      sx={{
                        textTransform: 'none',
                        backgroundColor: BASE_COLOR,
                        py: 1.2,
                        borderRadius: '10px',
                        fontWeight: 600,
                        '&:hover': { backgroundColor: '#0043ce' },
                      }}
                    >
                      Exportar PDF
                    </Button>
                    <Button
                      onClick={reset}
                      disabled={selectedCount === 0}
                      variant="text"
                      fullWidth
                      startIcon={<RestartAltIcon />}
                      sx={{ textTransform: 'none', color: 'text.secondary', borderRadius: '10px' }}
                    >
                      Reiniciar selección
                    </Button>
                  </Stack>
                </Box>
              </Paper>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: 'center', px: 1 }}>
                Los valores corresponden a costos cargados por el equipo de administración. Sujetos a actualización.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

const Costos = () => (
  <AdminGate>
    <CostosContent />
  </AdminGate>
);

export default Costos;
